import React, { useState } from 'react'
import { GEOSAMPA_CATEGORIES, buildLegendUrl } from './geosampaLayers'

function CategorySection({ category, activeLayers, onToggleLayer }) {
  const [isOpen, setIsOpen] = useState(false)
  const activeCount = category.layers.filter(l => activeLayers.has(l.id)).length

  return (
    <div className="border-b border-gray-600/30 last:border-b-0">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 flex justify-between items-center hover:bg-white/10 transition-colors text-left"
      >
        <span className="text-sm font-medium text-gray-100">
          {category.label}
          {activeCount > 0 && (
            <span className="ml-1.5 text-xs text-brand-lighter">({activeCount})</span>
          )}
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="px-3 pb-2 space-y-1">
          {category.layers.map(layer => (
            <LayerToggle
              key={layer.id}
              layer={layer}
              active={activeLayers.has(layer.id)}
              onToggle={() => onToggleLayer(layer.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function LayerToggle({ layer, active, onToggle }) {
  return (
    <div>
      <label className="flex items-center gap-2 cursor-pointer py-0.5">
        <input
          type="checkbox"
          checked={active}
          onChange={onToggle}
          className="w-3.5 h-3.5 rounded border-gray-500 bg-gray-700 text-brand-light focus:ring-brand-light focus:ring-offset-0"
        />
        <span className="text-xs text-gray-200">{layer.label}</span>
      </label>
      {active && <LegendImage layer={layer} />}
    </div>
  )
}

function LegendImage({ layer }) {
  const [errored, setErrored] = useState(false)
  if (errored) return null

  return (
    <div className="ml-6 mt-0.5 mb-1">
      <img
        src={buildLegendUrl(layer)}
        alt={`Legenda: ${layer.label}`}
        className="max-w-full"
        loading="lazy"
        onError={() => setErrored(true)}
      />
    </div>
  )
}

function AppLayerToggle({ label, checked, onChange, color, swatchType = 'square' }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer py-1">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="w-3.5 h-3.5 rounded border-gray-500 bg-gray-700 text-brand-light focus:ring-brand-light focus:ring-offset-0"
      />
      {color && swatchType === 'line' && (
        <span
          className="w-5 h-0 inline-block flex-shrink-0"
          style={{ borderTop: `2px solid ${color}` }}
        />
      )}
      {color && swatchType !== 'line' && (
        <span className="w-3 h-3 rounded-sm inline-block flex-shrink-0" style={{ backgroundColor: color }} />
      )}
      <span className="text-xs text-gray-200">{label}</span>
    </label>
  )
}

function AppLayerSection({ label, layers, onToggleAppLayer }) {
  return (
    <div className="px-3 py-2 border-b border-gray-600/30">
      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</span>
      <div className="mt-1 space-y-0">
        {layers.map(layer => (
          <AppLayerToggle
            key={layer.id}
            label={layer.label}
            checked={layer.visible}
            onChange={() => onToggleAppLayer(layer.id)}
            color={layer.color}
            swatchType={layer.swatchType}
          />
        ))}
      </div>
    </div>
  )
}

export default function GeosampaLayerPanel({ isOpen, onOpen, onClose, activeLayers, onToggleLayer, appLayers = [], appLayerSections, onToggleAppLayer }) {
  const sections = appLayerSections || [{ id: 'app-layers', label: 'Camadas do Mapa', layers: appLayers }]

  return (
    <>
      {/* Toggle button */}
      {!isOpen && (
        <button
          type="button"
          onClick={onOpen}
          className="absolute top-2.5 left-2.5 z-10 bg-white rounded shadow-md p-2 hover:bg-gray-100 transition-colors"
          title="Camadas GeoSampa"
        >
          <svg className="w-5 h-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
          </svg>
        </button>
      )}

      {/* Panel */}
      {isOpen && (
        <div
          className="absolute top-0 left-0 z-10 h-full w-72 flex flex-col"
          style={{ backgroundColor: 'rgba(30, 41, 59, 0.92)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-600/50">
            <h3 className="text-sm font-semibold text-white">Camadas GeoSampa</h3>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {sections.map(section => (
            <AppLayerSection
              key={section.id}
              label={section.label}
              layers={section.layers}
              onToggleAppLayer={onToggleAppLayer}
            />
          ))}

          {/* GeoSampa categories */}
          <div className="px-3 pt-2 pb-1">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Camadas GeoSampa</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {GEOSAMPA_CATEGORIES.map(cat => (
              <CategorySection
                key={cat.id}
                category={cat}
                activeLayers={activeLayers}
                onToggleLayer={onToggleLayer}
              />
            ))}
          </div>

          {/* Footer */}
          <div className="px-3 py-1.5 border-t border-gray-600/50 text-[10px] text-gray-500">
            Fonte: GeoSampa - Prefeitura de S&atilde;o Paulo
          </div>
        </div>
      )}
    </>
  )
}
